/**
 * Thin fetch wrapper for the four kernel endpoints the auth-origin app
 * needs to drive WebAuthn ceremonies. All endpoints live under
 * `/api/v1/auth/webauthn/...` on the suite kernel; the base URL is
 * configured at build time via `VITE_KERNEL_API_BASE`.
 *
 * In production the auth-origin runs on `login.update-fitness.ch` and the
 * kernel sits behind `api.update-fitness.ch`. Cross-origin requests are
 * acceptable here because:
 *   - `/webauthn/register/*` requires an authenticated session cookie;
 *     the user has already password-logged-in on the calling app's
 *     domain and the kernel issues cookies for both `.cloud` and `.ch`
 *     domains (verify during integration that SameSite + Domain are
 *     compatible — flagged for Phase 2 follow-up).
 *   - `/webauthn/login/*` is anonymous (the assertion proves identity)
 *     but is locked by per-route CORS to the auth-origin only on the
 *     kernel side.
 */

type AuthorizationProof =
  | { type: 'session' }
  | { type: 'assertion'; assertion_proof: string }
  | { type: 'recovery'; recovery_token: string };

export type BeginRegistrationBody = {
  label: string;
  authorization: AuthorizationProof;
};

export type FinishRegistrationBody = {
  challenge_id: string;
  credential: unknown;
  label: string;
};

export type BeginLoginBody = {
  username?: string;
  api_client_id: string;
  return_origin: string;
};

export type FinishLoginBody = {
  challenge_id: string;
  credential: unknown;
};

export type BeginRegistrationResponse = {
  challenge_id: string;
  public_key: Record<string, unknown>;
};

export type FinishRegistrationResponse = {
  credential: { id: string; label: string };
};

export type BeginLoginResponse = {
  challenge_id: string;
  public_key: Record<string, unknown>;
};

export type FinishLoginResponse = {
  sso_ticket: string;
  return_origin: string;
  expires_in: number;
};

const FALLBACK_API_BASE = '/api/v1';

export function api_base(): string {
  const configured =
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env['VITE_KERNEL_API_BASE'] as string | undefined)
      : undefined;
  return configured && configured !== '' ? configured : FALLBACK_API_BASE;
}

async function post_json<T>(path: string, body: unknown, with_credentials: boolean): Promise<T> {
  const response = await fetch(api_base() + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: with_credentials ? 'include' : 'omit',
    body: JSON.stringify(body),
  });
  if (response.ok === false) {
    let detail = '';
    try {
      const parsed = await response.json();
      detail =
        (parsed?.error?.code as string | undefined) ??
        (parsed?.error?.message as string | undefined) ??
        '';
    } catch {
      // ignore — non-json error body
    }
    throw new Error(detail !== '' ? detail : 'KERNEL_HTTP_' + String(response.status));
  }
  const wire = await response.json();
  // The kernel response envelope is { success, data, error, meta } per
  // kernel/api/api_response.php; unwrap to data.
  if (wire && typeof wire === 'object' && 'data' in wire) {
    return wire.data as T;
  }
  return wire as T;
}

/**
 * POST /api/v1/auth/webauthn/register/begin
 *
 * Auth: kernel session cookie required. with_credentials=true so the
 * browser sends the password-login cookies established on the calling
 * app's domain.
 */
export async function begin_registration(
  body: BeginRegistrationBody
): Promise<BeginRegistrationResponse> {
  return post_json<BeginRegistrationResponse>('/auth/webauthn/register/begin', body, true);
}

/**
 * POST /api/v1/auth/webauthn/register/finish
 */
export async function finish_registration(
  body: FinishRegistrationBody
): Promise<FinishRegistrationResponse> {
  return post_json<FinishRegistrationResponse>('/auth/webauthn/register/finish', body, true);
}

/**
 * POST /api/v1/auth/webauthn/login/begin
 *
 * Auth: anonymous — the assertion proves identity. credentials=omit so
 * we don't leak any session that happens to exist on the auth-origin
 * itself (the popup ceremony should not depend on prior sessions).
 */
export async function begin_login(body: BeginLoginBody): Promise<BeginLoginResponse> {
  return post_json<BeginLoginResponse>('/auth/webauthn/login/begin', body, false);
}

/**
 * POST /api/v1/auth/webauthn/login/finish
 */
export async function finish_login(body: FinishLoginBody): Promise<FinishLoginResponse> {
  return post_json<FinishLoginResponse>('/auth/webauthn/login/finish', body, false);
}
